import { Anima } from "kido/anima";
import { PointerMove } from "kido/pointer";
import { ResizeHub } from "kido/resize";
import { clamp, damp, queryAll, lerp } from "kido/utils";
import { WheelKeySubscription } from "kido/wheel";

import { App, type GpuInstance, type RouteMediaSlot } from "@app/context";
import { BasePage } from "@app/primitives/base-page";

import type Scene from "../gpu/scene";
import type { ScenePlane } from "../gpu/scene";

interface CardCarouselState {
  currentIndex: number;
  totalMedia: number;
  slug: string;
  planeId: string;
  planeIndex: number;
  plane: ScenePlane;
  mediaSlots: RouteMediaSlot[];
}

// Split ratio range: how much the columns shrink/grow on hover
const SPLIT_MIN = 0.4;
const SPLIT_MAX = 0.6;

// Base gap between cards (rem) - matches CSS
const BASE_GAP = 4.8;
// Max gap when scaled up (rem)
const MAX_GAP = 9.6;
// Scroll-bounds padding (rem): TOP offsets the container's CSS padding-top,
// BOTTOM is the visible gap below the last card at max scroll
const CONTAINER_PADDING_TOP = 11.2;
const CONTAINER_PADDING_BOTTOM = 3.2;

// Damping factor for the column split animation (higher = snappier)
const SPLIT_DAMP = 0.15;

// Damping factor for scroll
const SCROLL_DAMP = 0.09;

export class PlayPage extends BasePage {
  private pointer: PointerMove | null = null;
  private gridEl: HTMLElement | null = null;
  private leftCol: HTMLElement | null = null;
  private rightCol: HTMLElement | null = null;
  private targetSplit = 0.5;
  private currentSplit = 0.5;
  private gpu: GpuInstance | null;

  // Shared scroll state (pixel-based, converted to percentage per-column in loop)
  private scrollTarget = 0;
  private scrollCurrent = 0;
  private wheelSub: WheelKeySubscription | null = null;

  // Rem to px conversion
  private remToPx = 16;

  // Cached viewport height for performance
  private viewportHeight = 0;

  // Resize handler tracking
  private resizeId: symbol | null = null;

  // Carousel state per card
  private cardStates: CardCarouselState[] = [];
  private navHandler: ((e: Event) => void) | null = null;

  // Freeze split when hovering nav buttons
  private splitFrozen = false;

  // Block scroll/interaction until in() animation completes
  private ready = false;

  // Mouse leave handler
  private onMouseLeave: (() => void) | null = null;
  private _rafId = 0;

  // Capture-phase scroll blockers — active until in() completes
  private _blockWheel: ((e: Event) => void) | null = null;
  private _blockTouch: ((e: Event) => void) | null = null;

  // Live clock interval
  private clockInterval: ReturnType<typeof setInterval> | null = null;
  private clockEls: { el: HTMLElement; tz: string }[] = [];

  constructor() {
    super();
    this.gpu = App?.gpu;
  }

  async init(container: Element | null): Promise<void> {
    if (!container) return;
    await super.init(container);

    const root = container as HTMLElement;

    // Mobile / tablet viewport (matches CSS `tablet` breakpoint): skip all
    // desktop column machinery
    const isMobileViewport =
      (window.innerWidth || document.documentElement.clientWidth) < 1025;
    if (isMobileViewport) {
      this.ready = true;
      this.initClocks(root);
      return;
    }

    // Block all scroll input at capture phase until in() animation finishes
    this.blockScroll();
    window.scrollTo(0, 0);

    this.gridEl = root.querySelector(".pl-m");

    if (!this.gridEl) return;

    // Columns are pre-baked in HTML
    this.leftCol = this.gridEl.querySelector(".pl-col--left");
    this.rightCol = this.gridEl.querySelector(".pl-col--right");

    if (!this.leftCol || !this.rightCol) return;

    // Get rem to px conversion
    this.remToPx = parseFloat(
      getComputedStyle(document.documentElement).fontSize
    );

    // Cache viewport height
    this.viewportHeight = window.innerHeight;

    // Register resize handler
    this.resizeId = ResizeHub.add(() => this.handleResize());

    this.pointer = new PointerMove({
      cb: (x: number) => this.onPointerMove(x),
    });

    // Reset split to 50/50 when cursor leaves the browser window
    this.onMouseLeave = () => {
      this.targetSplit = 0.5;
      App.gpu?.requestExtraFrames?.();
    };
    document.addEventListener("mouseleave", this.onMouseLeave);

    // Shared wheel subscription — active from init so preventDefault blocks
    // native scroll; onWheel discards deltas while this.ready === false
    this.wheelSub = new WheelKeySubscription({
      cb: (delta: number) => this.onWheel(delta),
    });

    if (this.gpu) {
      // Restore shared scene planes when coming back from a play detail page
      const ts = App.transitState;
      if (ts?.direction === "to-play") {
        const sceneKey = "/play";
        const scene =
          (App.gpu?.scenes as Record<string, Scene>)?.[sceneKey] ?? null;
        if (scene) {
          scene.restoreForTransit(ts.slug, ts.planeId);
          scene.updateBounds();
          App.gpu?.requestRedraw?.();
        }
        App.transitState = null;
      }
    }

    App.scroller?.stop();

    if (this.gpu) {
      document.documentElement.style.overflow = "hidden";
      const appEl = document.getElementById("app");
      if (appEl) appEl.style.position = "fixed";

      // Register as pre-render callback so transforms are applied
      // before the GPU renderer reads DOM bounds
      App.gpu?.setPreRender?.(() => this.loop());

      // Ensure scene planes exist and DOM slots are fresh.
      // On initial load, textures may not have been uploaded when the
      // scene was first constructed. On navigation back, the DOM is new
      // but the cached scene still points to removed elements.
      const playScene =
        (App.gpu?.scenes as Record<string, Scene>)?.["/play"] ?? null;
      if (playScene) {
        playScene.setup();
      }

      // Set up carousel state for each play card
      this.initCarousel(root);

      // Hide nav arrows and titles — fly-in will fade them in
      const allNavs = this.gridEl ? queryAll(this.gridEl, ".pl-el_n") : [];
      const allTitles = this.gridEl ? queryAll(this.gridEl, ".pl-el_i_e") : [];
      for (const el of [...allNavs, ...allTitles]) {
        (el as HTMLElement).style.opacity = "0";
      }

      this.gpu?.freezeBounds?.(true);
      this.loop();

      const scene =
        (App.gpu?.scenes as Record<string, Scene>)?.["/play"] ?? null;
      const domSlots = scene?.domSlots ?? [];
      const planes = scene?.planes ?? [];
      const vh = window.innerHeight;

      for (let i = 0; i < planes.length; i++) {
        const plane = planes[i];
        const slot = domSlots[i];
        if (!plane || !slot) continue;

        const rect = slot.getBoundingClientRect();

        if (!plane.boundsPx) {
          plane.boundsPx = { x: 0, y: 0, w: 0, h: 0, z: 0 };
        }

        plane.boundsPx.x = rect.x;
        plane.boundsPx.y = rect.y + vh;
        plane.boundsPx.w = rect.width;
        plane.boundsPx.h = rect.height;
        plane.opacity = 0;
      }
    } else {
      // Desktop no-GPU: hide elements for fly-in, block scroll until in() completes
      document.documentElement.style.overflow = "hidden";
      const appEl = document.getElementById("app");
      if (appEl) appEl.style.position = "fixed";

      const cards = this.gridEl ? queryAll(this.gridEl, ".pl-el") : [];
      for (const card of cards) {
        (card as HTMLElement).style.opacity = "0";
      }

      this.loop();
      this._startRafLoop();
    }

    // Start live clocks
    this.initClocks(root);
  }

  private initClocks(root: HTMLElement): void {
    const els = queryAll(root, ".pl-footer_clock");
    this.clockEls = [];
    for (const el of els) {
      const tz = (el as HTMLElement).getAttribute("data-tz");
      if (tz) {
        this.clockEls.push({ el: el as HTMLElement, tz });
      }
    }
    if (this.clockEls.length) {
      this.updateClocks();
      this.clockInterval = setInterval(() => this.updateClocks(), 1000);
    }
  }

  private updateClocks(): void {
    for (const { el, tz } of this.clockEls) {
      const now = new Date();
      let timeStr: string;
      try {
        timeStr = now.toLocaleTimeString("en-US", {
          timeZone: tz,
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });
      } catch {
        timeStr = now.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });
      }
      const parts = timeStr.split(":");
      if (parts.length >= 2) {
        el.innerHTML = `${parts[0]}<span class="pl-footer_blink">:</span>${parts.slice(1).join(":")}`;
      } else {
        el.textContent = timeStr;
      }
    }
  }

  private onPointerMove(x: number): void {
    if (!this.ready) return;

    const vw = window.innerWidth;
    const ratio = x / vw;

    if (this.splitFrozen) return;

    const clamped = clamp(ratio, 0.3, 0.7);
    const linear = (clamped - 0.3) / 0.4;
    const t =
      linear < 0.5
        ? Math.pow(2, 20 * linear - 10) / 2
        : (2 - Math.pow(2, -20 * linear + 10)) / 2;
    this.targetSplit = SPLIT_MAX - t * (SPLIT_MAX - SPLIT_MIN);
  }

  private onWheel(delta: number): void {
    if (!this.ready) return;
    this.scrollTarget += delta;
  }

  private handleResize(): void {
    this.viewportHeight = window.innerHeight;
    this.remToPx = parseFloat(
      getComputedStyle(document.documentElement).fontSize
    );
  }

  private blockScroll(): void {
    this._blockWheel = (e: Event) => {
      if (e.cancelable) e.preventDefault();
    };
    this._blockTouch = (e: Event) => {
      if (e.cancelable) e.preventDefault();
    };
    window.addEventListener("wheel", this._blockWheel, {
      passive: false,
      capture: true,
    });
    window.addEventListener("touchmove", this._blockTouch, {
      passive: false,
      capture: true,
    });
  }

  private unblockScroll(): void {
    if (this._blockWheel) {
      window.removeEventListener("wheel", this._blockWheel, true);
      this._blockWheel = null;
    }
    if (this._blockTouch) {
      window.removeEventListener("touchmove", this._blockTouch, true);
      this._blockTouch = null;
    }
  }

  private _startRafLoop(): void {
    const tick = () => {
      this.loop();
      this._rafId = requestAnimationFrame(tick);
    };
    this._rafId = requestAnimationFrame(tick);
  }

  private _stopRafLoop(): void {
    if (this._rafId) {
      cancelAnimationFrame(this._rafId);
      this._rafId = 0;
    }
  }

  private initCarousel(root: HTMLElement): void {
    const scene = (App.gpu?.scenes as Record<string, Scene>)?.["/play"] ?? null;
    const planes = scene?.planes ?? [];
    const domSlots = scene?.domSlots ?? [];
    const cards = queryAll(root, ".pl-el");

    for (const card of cards) {
      const el = card as HTMLElement;
      const slug = el.getAttribute("data-slug");
      if (!slug) continue;
      const figure = el.querySelector("figure");
      if (!figure) continue;

      const planeId = figure.getAttribute("data-plane") ?? "";

      let planeIndex = -1;
      for (let i = 0; i < domSlots.length; i++) {
        const slot = domSlots[i] as HTMLElement | null;
        if (slot?.getAttribute("data-plane") === planeId) {
          planeIndex = i;
          break;
        }
      }

      if (planeIndex < 0 || !planes[planeIndex]) continue;

      const mediaStore = App.gpu?.media?.[`/play/${slug}`];
      const mediaSlots = mediaStore?.main ?? [];

      const planeHasVideo = !!planes[planeIndex]?.videoTexture;
      const usableCount = mediaSlots.filter(
        (s) => s.attrib[0] != null || (s.type === "video" && planeHasVideo)
      ).length;
      const total = Math.max(1, usableCount);

      this.cardStates.push({
        currentIndex: 0,
        totalMedia: total,
        slug,
        planeId,
        planeIndex,
        plane: planes[planeIndex]!,
        mediaSlots,
      });
    }

    if (this.gridEl) {
      this.navHandler = (e: Event) => this.onNavClick(e);
      this.gridEl.addEventListener("click", this.navHandler);
    }

    const navEls = queryAll(root, ".pl-el_n");
    for (const nav of navEls) {
      nav.addEventListener("mouseenter", () => {
        this.splitFrozen = true;
      });
      nav.addEventListener("mouseleave", () => {
        this.splitFrozen = false;
      });
    }
  }

  private onNavClick(e: Event): void {
    const target = e.target as HTMLElement;
    const btn = target.closest(".pl-el_n_a") as HTMLElement | null;
    if (!btn) return;

    e.preventDefault();
    e.stopPropagation();

    const dir = btn.getAttribute("data-dir");
    const card = btn.closest(".pl-el") as HTMLElement | null;
    if (!card || !dir) return;

    const figure = card.querySelector("figure");
    const planeId = figure?.getAttribute("data-plane") ?? "";
    if (!planeId) return;

    const state = this.cardStates.find((s) => s.planeId === planeId);
    if (!state) return;

    // Recalculate usable count dynamically — deferred textures may have
    // loaded after initCarousel ran, so the cached totalMedia can be stale.
    const hasVideo = !!state.plane.videoTexture;
    const liveCount = state.mediaSlots.filter(
      (s) => s.attrib[0] != null || (s.type === "video" && hasVideo)
    ).length;
    if (liveCount <= 1) return;

    const step = dir === "next" ? 1 : -1;

    let nextIndex = state.currentIndex;
    for (let attempts = 0; attempts < state.mediaSlots.length; attempts++) {
      nextIndex =
        (nextIndex + step + state.mediaSlots.length) % state.mediaSlots.length;
      const slot = state.mediaSlots[nextIndex];
      if (!slot) continue;
      if (slot.attrib[0] != null) break;
      if (slot.type === "video" && hasVideo) break;
    }

    state.currentIndex = nextIndex;
    const slot = state.mediaSlots[nextIndex];
    if (!slot) return;

    const vt = state.plane.videoTexture;
    if (slot.type === "video" && vt) {
      vt.onFrame(() => {
        state.plane.texture = vt.texture;
        App.gpu?.requestRedraw?.();
      });
      vt.play();
      if (vt.texture) {
        state.plane.texture = vt.texture;
      }
    } else if (slot.attrib[0]) {
      if (vt) {
        vt.pause();
        vt.onFrame(null);
      }
      state.plane.texture = slot.attrib[0];
      state.plane.sampler = slot.sampler;
      if (slot.wh) state.plane.aspectRatio = slot.wh;
    } else {
      return;
    }

    App.gpu?.requestRedraw?.();
  }

  private loop(): void {
    if (!this.leftCol || !this.rightCol || !this.gridEl) return;

    this.currentSplit = damp(this.currentSplit, this.targetSplit, SPLIT_DAMP);

    const leftScale = this.currentSplit / 0.5;
    const rightScale = (1 - this.currentSplit) / 0.5;

    const leftVisualGap =
      BASE_GAP + (leftScale - 0.8) * ((MAX_GAP - BASE_GAP) / 0.4);
    const rightVisualGap =
      BASE_GAP + (rightScale - 0.8) * ((MAX_GAP - BASE_GAP) / 0.4);

    const leftGap = leftVisualGap / leftScale;
    const rightGap = rightVisualGap / rightScale;

    this.leftCol.style.gap = `${leftGap}rem`;
    this.rightCol.style.gap = `${rightGap}rem`;

    // Scroll bounds in unscaled content pixels
    const paddingPx =
      (CONTAINER_PADDING_TOP + CONTAINER_PADDING_BOTTOM) * this.remToPx;
    const leftBoundsMax = Math.max(
      0,
      this.leftCol.scrollHeight - this.viewportHeight + paddingPx
    );
    const rightBoundsMax = Math.max(
      0,
      this.rightCol.scrollHeight - this.viewportHeight + paddingPx
    );

    // Clamp shared scroll target to the larger column's bounds
    const maxBounds = Math.max(leftBoundsMax, rightBoundsMax);
    this.scrollTarget = clamp(this.scrollTarget, 0, maxBounds);
    this.scrollCurrent = damp(
      this.scrollCurrent,
      this.scrollTarget,
      SCROLL_DAMP
    );

    // Both columns track the same scroll percentage
    const scrollPercent = maxBounds > 0 ? this.scrollCurrent / maxBounds : 0;

    const leftScrollPx = scrollPercent * leftBoundsMax;
    const rightScrollPx = scrollPercent * rightBoundsMax;

    const leftYRatio = leftBoundsMax > 0 ? leftScrollPx / leftBoundsMax : 0;
    const rightYRatio = rightBoundsMax > 0 ? rightScrollPx / rightBoundsMax : 0;

    const halfVh = this.viewportHeight * 0.5;
    this.leftCol.style.transformOrigin = `left ${leftScrollPx + halfVh}px`;
    this.rightCol.style.transformOrigin = `right ${rightScrollPx + halfVh}px`;

    const leftOffset = halfVh * (1 - leftScale) * (leftYRatio * 2 - 1);
    const rightOffset = halfVh * (1 - rightScale) * (rightYRatio * 2 - 1);

    this.leftCol.style.transform = `translate3d(0, ${-leftScrollPx + leftOffset}px, 0) scale(${leftScale})`;
    this.rightCol.style.transform = `translate3d(0, ${-rightScrollPx + rightOffset}px, 0) scale(${rightScale})`;

    const maxVisualHeight = Math.max(
      this.leftCol.scrollHeight * leftScale,
      this.rightCol.scrollHeight * rightScale
    );
    this.gridEl.style.height = `${maxVisualHeight}px`;

    const splitAnimating =
      Math.abs(this.currentSplit - this.targetSplit) > 0.0005;
    const scrollAnimating =
      Math.abs(this.scrollCurrent - this.scrollTarget) > 0.5;

    if (splitAnimating || scrollAnimating) {
      App.gpu?.requestExtraFrames?.();
    }
  }

  async in(): Promise<void> {
    if (!this.leftCol || !this.rightCol) return;

    if (!this.gpu) {
      // Desktop no-GPU: DOM-only staggered fly-in (same timing as GPU path)
      const vh = window.innerHeight;
      const duration = 1000;
      const baseStagger = 80;
      const staggerDecay = 0.3;
      const getDelay = (s: number): number => {
        if (s <= 0) return 0;
        return (
          (baseStagger * (1 - Math.pow(staggerDecay, s))) / (1 - staggerDecay)
        );
      };

      // Build round-robin stagger order from DOM cards
      const leftCards = this.leftCol ? queryAll(this.leftCol, ".pl-el") : [];
      const rightCards = this.rightCol ? queryAll(this.rightCol, ".pl-el") : [];
      const maxLen = Math.max(leftCards.length, rightCards.length);
      const staggerOrder: HTMLElement[] = [];
      for (let row = 0; row < maxLen; row++) {
        if (row < leftCards.length)
          staggerOrder.push(leftCards[row] as HTMLElement);
        if (row < rightCards.length)
          staggerOrder.push(rightCards[row] as HTMLElement);
      }

      if (!staggerOrder.length) {
        this.scrollTarget = 0;
        this.scrollCurrent = 0;
        this.unblockScroll();
        this.ready = true;
        this.pointer?.on();
        this.wheelSub?.on();
        requestAnimationFrame(() => {
          document.documentElement.style.overflow = "";
          const appEl = document.getElementById("app");
          if (appEl) appEl.style.position = "";
        });
        return;
      }

      await new Promise<void>((resolve) => {
        let completed = 0;
        const total = staggerOrder.length;

        for (let s = 0; s < staggerOrder.length; s++) {
          const card = staggerOrder[s]!;
          const delay = getDelay(s);

          new Anima({
            el: card,
            p: { o: [0, 1, ""], y: [vh, 0, "px"] },
            d: duration,
            e: "oQ",
            de: delay,
            cb: () => {
              completed++;
              if (completed >= total) {
                this.scrollTarget = 0;
                this.scrollCurrent = 0;
                this.unblockScroll();
                this.pointer?.on();
                this.wheelSub?.on();
                this.ready = true;
                // Defer scroll-lock removal by one frame so WheelKeyDispatcher
                // can preventDefault() before native scroll fires
                requestAnimationFrame(() => {
                  const appEl = document.getElementById("app");
                  if (appEl) appEl.style.position = "";
                  document.documentElement.style.overflow = "";
                });
                resolve();
              }
            },
          }).play();

          const uiEls = [
            card.querySelector(".pl-el_i_e"),
            card.querySelector(".pl-el_n"),
            card.querySelector(".pl-el_dot"),
          ].filter(Boolean) as HTMLElement[];
          for (const el of uiEls) {
            new Anima({
              el,
              p: { opacity: [0, 1], y: [50, 0, "%"] },
              d: 400,
              e: "oQ",
              de: delay + 600,
            }).play();
          }
        }
      });
      return;
    }

    // Pause global scroller — we handle scroll ourselves
    App.scroller?.pause?.();

    // Staggered fly-in from bottom
    const scene = (App.gpu?.scenes as Record<string, Scene>)?.["/play"] ?? null;
    const domSlots = scene?.domSlots ?? [];
    const planes = scene?.planes ?? [];
    if (!planes.length) {
      this.scrollTarget = 0;
      this.scrollCurrent = 0;
      this.unblockScroll();
      this.ready = true;
      this.pointer?.on();
      this.wheelSub?.on();
      this.gpu?.freezeBounds?.(false);
      requestAnimationFrame(() => {
        document.documentElement.style.overflow = "";
        const appEl = document.getElementById("app");
        if (appEl) appEl.style.position = "";
      });
      return;
    }

    const vh = window.innerHeight;
    const duration = 1000;
    const baseStagger = 80;
    const staggerDecay = 0.3;
    const getDelay = (s: number): number => {
      if (s <= 0) return 0;
      return (
        (baseStagger * (1 - Math.pow(staggerDecay, s))) / (1 - staggerDecay)
      );
    };

    // Build round-robin stagger order: left-col items interleaved with right-col
    const leftItems: number[] = [];
    const rightItems: number[] = [];
    const leftSlots = this.leftCol ? queryAll(this.leftCol, ".g__l") : [];
    const rightSlots = this.rightCol ? queryAll(this.rightCol, ".g__l") : [];

    for (let i = 0; i < domSlots.length; i++) {
      const slot = domSlots[i];
      if (!slot) continue;
      if (leftSlots.includes(slot as HTMLElement)) leftItems.push(i);
      else if (rightSlots.includes(slot as HTMLElement)) rightItems.push(i);
    }

    const maxLen = Math.max(leftItems.length, rightItems.length);
    const staggerOrder: number[] = [];
    for (let row = 0; row < maxLen; row++) {
      if (row < leftItems.length) staggerOrder.push(leftItems[row]!);
      if (row < rightItems.length) staggerOrder.push(rightItems[row]!);
    }

    // Collect UI elements per plane
    const titles: (HTMLElement | null)[] = [];
    const navs: (HTMLElement | null)[] = [];
    const dots: (HTMLElement | null)[] = [];
    for (let i = 0; i < domSlots.length; i++) {
      const slot = domSlots[i] as HTMLElement | null;
      const card = slot?.closest(".pl-el");
      titles[i] = card?.querySelector(".pl-el_i_e") as HTMLElement | null;
      navs[i] = card?.querySelector(".pl-el_n") as HTMLElement | null;
      dots[i] = card?.querySelector(".pl-el_dot") as HTMLElement | null;
    }

    // Animate each plane from below viewport to its final position
    await new Promise<void>((resolve) => {
      let completed = 0;
      const total = staggerOrder.length || planes.length;
      const indices = staggerOrder.length
        ? staggerOrder
        : planes.map((_, i) => i);

      for (let s = 0; s < indices.length; s++) {
        const i = indices[s]!;
        const plane = planes[i];
        const slot = domSlots[i];
        if (!plane || !slot) {
          completed++;
          if (completed >= total) resolve();
          continue;
        }

        const rect = (slot as HTMLElement).getBoundingClientRect();
        const finalY = rect.y;
        const startY = finalY + vh;

        plane.opacity = 1;

        const delay = getDelay(s);

        new Anima({
          d: duration,
          e: "oQ",
          de: delay,
          u: (v) => {
            const t = v.prE;
            if (plane.boundsPx) {
              plane.boundsPx.y = lerp(startY, finalY, t);
            }
            App.gpu?.requestRedraw?.();
          },
          cb: () => {
            if (plane.boundsPx) {
              plane.boundsPx.y = finalY;
            }
            plane.opacity = 1;
            completed++;
            if (completed >= total) {
              this.scrollTarget = 0;
              this.scrollCurrent = 0;
              this.unblockScroll();
              this.pointer?.on();
              this.wheelSub?.on();
              this.ready = true;
              this.gpu?.freezeBounds?.(false);
              // Defer scroll-lock removal by one frame so WheelKeyDispatcher
              // can preventDefault() before native scroll fires
              requestAnimationFrame(() => {
                const appEl = document.getElementById("app");
                if (appEl) appEl.style.position = "";
                document.documentElement.style.overflow = "";
              });
              resolve();
            }
          },
        }).play();

        // Fade in UI elements right before the plane animation completes
        const uiEls = [titles[i], navs[i], dots[i]].filter(
          Boolean
        ) as HTMLElement[];
        for (const el of uiEls) {
          new Anima({
            el,
            p: { opacity: [0, 1], y: [50, 0, "%"] },
            d: 400,
            e: "oQ",
            de: delay + 600,
          }).play();
        }
      }

      if (total === 0) {
        this.ready = true;
        this.gpu?.freezeBounds?.(false);
        resolve();
      }
    });
  }

  async out(): Promise<void> {
    if (!this.gpu) {
      document.documentElement.style.overflow = "";
      const appEl = document.getElementById("app");
      if (appEl) appEl.style.position = "";
      this.destroy();
      return;
    }

    document.documentElement.style.overflow = "";
    const appEl = document.getElementById("app");
    if (appEl) appEl.style.position = "";

    // Pass clicked plane info + bounds to play-detail page
    if (App.route.new.page === "play-detail") {
      const anchor = App.target as HTMLElement | null;
      const card = anchor?.closest?.(".pl-el") as HTMLElement | null;
      const slug = card?.getAttribute("data-slug") ?? null;
      const figure = card?.querySelector("figure") as HTMLElement | null;
      const planeId = figure?.getAttribute("data-plane") ?? null;
      if (planeId) {
        const scene =
          (App.gpu?.scenes as Record<string, Scene>)?.["/play"] ?? null;
        const planes = scene?.planes ?? [];
        const clickedPlane = planes.find((p) => p.id === planeId);
        const coverBounds = clickedPlane?.boundsPx
          ? {
              x: clickedPlane.boundsPx.x,
              y: clickedPlane.boundsPx.y,
              w: clickedPlane.boundsPx.w,
              h: clickedPlane.boundsPx.h,
            }
          : undefined;
        App.transitState = {
          coverBounds,
          slug: slug ?? "",
          planeId,
          direction: "to-play-detail",
        };

        // Fade out non-clicked grid planes + UI before DOM swap
        this.pointer?.off();
        this.wheelSub?.off();
        App.gpu?.setPreRender?.(null);
        App.gpu?.freezeBounds?.(true);

        const uiEls = this.gridEl
          ? queryAll(
              this.gridEl,
              ".pl-el_i_e, .pl-el_n, .pl-el_dot, .pl-footer_left, .pl-footer_right"
            )
          : [];
        for (const el of uiEls) {
          new Anima({
            el: el as HTMLElement,
            p: { opacity: [1, 0] },
            d: 300,
            e: "oQ",
          }).play();
        }

        const nonClicked = planes.filter((p) => p !== clickedPlane);
        if (nonClicked.length) {
          await new Promise<void>((resolve) => {
            let done = 0;
            for (const p of nonClicked) {
              const startO = p.opacity ?? 1;
              new Anima({
                d: 400,
                e: "oQ",
                u: (v) => {
                  p.opacity = startO * (1 - v.prE);
                  App.gpu?.requestRedraw?.();
                },
                cb: () => {
                  p.opacity = 0;
                  done++;
                  if (done >= nonClicked.length) resolve();
                },
              }).play();
            }
          });
        }
      }
    }

    this.destroy();
  }

  private destroy(): void {
    this._stopRafLoop();
    this.unblockScroll();

    // Restore all planes to cover textures and re-enable video playback
    for (const state of this.cardStates) {
      if (state.currentIndex !== 0 && state.mediaSlots[0]) {
        const cover = state.mediaSlots[0];
        if (cover.attrib[0]) {
          state.plane.texture = cover.attrib[0];
          state.plane.sampler = cover.sampler;
          if (cover.wh) state.plane.aspectRatio = cover.wh;
        }
      }
      const vt = state.plane.videoTexture;
      if (vt && vt.paused) {
        vt.onFrame(() => {
          state.plane.texture = vt.texture;
          App.gpu?.requestRedraw?.();
        });
        if (App.gpu?.videosPlaying) {
          vt.play();
        }
      }
    }
    this.cardStates = [];

    if (this.gridEl && this.navHandler) {
      this.gridEl.removeEventListener("click", this.navHandler);
      this.navHandler = null;
    }

    if (this.clockInterval) {
      clearInterval(this.clockInterval);
      this.clockInterval = null;
    }
    this.clockEls = [];

    this.pointer?.off();
    this.pointer = null;
    this.wheelSub?.off();
    this.wheelSub = null;

    if (this.onMouseLeave) {
      document.removeEventListener("mouseleave", this.onMouseLeave);
      this.onMouseLeave = null;
    }

    App.scroller?.start?.();
    App.scroller?.resume?.();

    App.gpu?.setPreRender?.(null);

    if (this.resizeId) {
      ResizeHub.remove(this.resizeId);
      this.resizeId = null;
    }

    this.leftCol = null;
    this.rightCol = null;
    this.gridEl = null;

    this.ready = false;
    this.splitFrozen = false;
    this.targetSplit = 0.5;
    this.currentSplit = 0.5;
    this.viewportHeight = 0;
    this.scrollTarget = 0;
    this.scrollCurrent = 0;
  }
}
